import { useRoles } from "@/hooks/useTeam";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Shield } from "lucide-react";

interface RolesTabProps {
  accountId: string;
}

export function RolesTab({ accountId }: RolesTabProps) {
  const { data: roles, isLoading } = useRoles(accountId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!roles?.length) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No roles found</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {roles.map((role) => (
        <Card key={role._id}>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  {role.name}
                  {role.isDefault && (
                    <Badge variant="secondary" className="text-xs">
                      Default
                    </Badge>
                  )}
                </CardTitle>
                {role.description && (
                  <CardDescription>{role.description}</CardDescription>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div>
              <p className="text-sm font-medium mb-2">Permissions:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(role.permissions)
                  .filter(([_, granted]) => granted)
                  .map(([permission]) => (
                    <Badge key={permission} variant="outline">
                      {permission.replace(/_/g, " ")}
                    </Badge>
                  ))}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
